/* ═══════════════════════════════════════════════════════════════════════════
   VREME U TAHOGRAFU — jedno mesto za pretvaranje UTC ↔ lokalno.

   Tahograf beleži SVE u UTC. Kontrolni izveštaj (Tachospeed) i inspekcija
   gledaju LOKALNO vreme — u zaglavlju njihovog izveštaja piše
   „Izveštaj generisan za CET/CEST".

   Provereno na pravim podacima:
       baza (UTC)        2026-06-22 23:25
       Beograd (CEST)    2026-06-23 01:25
       Tachospeed kaže   23.06.2026 01:25   ← isto kao Beograd

   ───────────────────────────────────────────────────────────────────────────
   TRIK KOJI SE OVDE KORISTI

   `uZonu(ms)` vraća POMERENI trenutak čije UTC komponente odgovaraju lokalnom
   zidnom satu. Zahvaljujući tome sav postojeći kod koji radi preko `Date.UTC`,
   `getUTC*` ili `toISOString().substring(0,10)` postaje lokalno ispravan bez
   prepisivanja — samo mu se pre toga vrednost provuče kroz `uZonu`.

   ŠTA SE NE POMERA: trajanja. Trajanje je razlika dva stvarna trenutka
   (`kraj - pocetak`) i računa se nad NEPOMERENIM vrednostima. U noći prelaska
   na letnje/zimsko vreme zidni sat preskoči ili ponovi sat, pa bi pomereno
   trajanje bilo pogrešno za 60 minuta. Odmor od 9h mora ostati 9h stvarnog
   vremena, bez obzira šta sat pokazuje.

   Dakle: GRANICE i NATPISI idu kroz zonu, TRAJANJA ne.
   ═══════════════════════════════════════════════════════════════════════════ */

/* Zona u kojoj se tumači tahografsko vreme. Tachospeed koristi jednu zonu za
   ceo izveštaj (ne menja je po državi u kojoj se vozač zatekao), pa je i ovde
   jedna. Ako ikad zatreba po firmi, ova konstanta postaje parametar. */
export const ZONA = 'Europe/Belgrade';

/* Kratak natpis za ose i zaglavlja — „CET" zimi, „CEST" leti. */
export function oznakaZone(ms = Date.now(), zona = ZONA) {
    try {
        const d = new Intl.DateTimeFormat('en-GB', { timeZone: zona, timeZoneName: 'short' })
            .formatToParts(new Date(ms)).find(p => p.type === 'timeZoneName');
        return d ? d.value : zona;
    } catch { return zona; }
}

/* Jedan formatter po zoni — pravljenje Intl objekta je skupo, a dijagram ume da
   traži pomak nekoliko hiljada puta. */
const formatteri = new Map();
function formatter(zona) {
    if (!formatteri.has(zona)) {
        formatteri.set(zona, new Intl.DateTimeFormat('en-US', {
            timeZone: zona, hour12: false,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        }));
    }
    return formatteri.get(zona);
}

/* Pomak zone u milisekundama, za DATI trenutak — tako se letnje/zimsko vreme
   rešava samo od sebe, bez tabela. */
const pomakKes = new Map();
export function pomakMs(ms, zona = ZONA) {
    /* keširanje po satu: pomak se menja samo na prelazu, a i tada tačno na pun sat */
    const kljuc = zona + '|' + Math.floor(ms / 3600000);
    if (pomakKes.has(kljuc)) return pomakKes.get(kljuc);

    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return 0;
    const p = formatter(zona).formatToParts(d)
        .reduce((a, x) => { a[x.type] = x.value; return a; }, {});
    const kaoUtc = Date.UTC(+p.year, +p.month - 1, +p.day, (+p.hour) % 24, +p.minute, +p.second);
    const pomak = kaoUtc - (ms - (ms % 1000));

    if (pomakKes.size > 20000) pomakKes.clear();   // zaštita od rasta u dugoj sesiji
    pomakKes.set(kljuc, pomak);
    return pomak;
}

/** UTC trenutak → pomereni trenutak čije UTC komponente su lokalni zidni sat. */
export function uZonu(ms, zona = ZONA) {
    const n = typeof ms === 'number' ? ms : Date.parse(ms);
    return Number.isFinite(n) ? n + pomakMs(n, zona) : NaN;
}

/**
 * Obrnuto: lokalni zidni sat (kao pomereni ms) → stvarni UTC trenutak.
 *
 * PAŽNJA — jedan sat godišnje nije jednoznačan. U noći prelaska na zimsko vreme
 * (poslednja nedelja oktobra) sat se vrati, pa lokalno „02:30" postoji DVAPUT —
 * jednom kao CEST, jednom kao CET. Nijedna funkcija ne može da pogodi koji je
 * mislio; ova vraća prvi.
 *
 * Zato se `izZone` ovde koristi SAMO za lokalnu ponoć (`pocetakLokalnogDana`),
 * a prelaz je u 03:00 — ponoć nikad ne pada u dvoznačni sat. Ne koristiti je za
 * proizvoljna vremena bez te provere.
 */
export function izZone(pomereno, zona = ZONA) {
    const n = typeof pomereno === 'number' ? pomereno : Date.parse(pomereno);
    if (!Number.isFinite(n)) return NaN;
    /* Pomak se traži za približan trenutak pa se jednom popravi — dovoljno, jer
       se pomak menja najviše jednom u pola godine. */
    const prvi = n - pomakMs(n, zona);
    return n - pomakMs(prvi, zona);
}

/** Lokalni datum „YYYY-MM-DD". */
export function lokalniDan(ms, zona = ZONA) {
    const p = uZonu(ms, zona);
    return Number.isFinite(p) ? new Date(p).toISOString().substring(0, 10) : '';
}

/** Lokalni ISO zapis (bez Z, jer to više nije UTC nego zidni sat). */
export function lokalniIso(ms, zona = ZONA) {
    const p = uZonu(ms, zona);
    return Number.isFinite(p) ? new Date(p).toISOString().substring(0, 19) : '';
}

/** Minuti od lokalne ponoći — položaj na osi dijagrama. */
export function lokalniMinut(ms, zona = ZONA) {
    const p = uZonu(ms, zona);
    if (!Number.isFinite(p)) return null;
    const d = new Date(p);
    return d.getUTCHours() * 60 + d.getUTCMinutes();
}

/** UTC trenutak lokalne ponoći datog dana („YYYY-MM-DD"). */
export function pocetakLokalnogDana(dan, zona = ZONA) {
    return izZone(Date.parse(String(dan).substring(0, 10) + 'T00:00:00.000Z'), zona);
}

/* ── natpisi ──────────────────────────────────────────────────────────────── */

export function fmtDatum(ms, zona = ZONA) {
    const d = new Date(uZonu(ms, zona));
    if (Number.isNaN(d.getTime())) return '';
    return `${String(d.getUTCDate()).padStart(2, '0')}.${String(d.getUTCMonth() + 1).padStart(2, '0')}.${d.getUTCFullYear()}`;
}

export function fmtVreme(ms, zona = ZONA) {
    const d = new Date(uZonu(ms, zona));
    if (Number.isNaN(d.getTime())) return '';
    return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

export function fmtDatumVreme(ms, zona = ZONA) {
    const d = fmtDatum(ms, zona);
    return d ? `${d} ${fmtVreme(ms, zona)}` : '';
}

/* ── pomeranje celih zapisa ───────────────────────────────────────────────── */

/**
 * Prebacuje aktivnosti sa /tacho/faults u LOKALNO vreme, zadržavajući isti oblik
 * (ISO stringovi). Posle ovoga grupisanje po `ActivityDate` i sečenje
 * `substring(11,16)` daju lokalni dan i sat — pa dijagram ne mora da se menja.
 *
 * Aktivnost koja prelazi ponoć po lokalnom vremenu MENJA DAN (npr. 22.06 23:25 UTC
 * postaje 23.06 01:25) — upravo tako je prikazuje i kontrolni izveštaj.
 */
export function zapisiULokalno(zapisi, zona = ZONA) {
    return (Array.isArray(zapisi) ? zapisi : []).map(r => {
        const od = Date.parse(r.StartTime);
        const doo = Date.parse(r.EndTime);
        if (!Number.isFinite(od)) return r;
        const odL = lokalniIso(od, zona);
        return {
            ...r,
            StartTime: odL + '.000Z',
            EndTime: Number.isFinite(doo) ? lokalniIso(doo, zona) + '.000Z' : r.EndTime,
            ActivityDate: odL.substring(0, 10) + 'T00:00:00.000Z',
            /* stvarni UTC ostaje dostupan, ako zatreba */
            _utcStart: r.StartTime,
            _utcEnd: r.EndTime,
        };
    });
}

/**
 * Segmenti zapisani kao `ActivityDate` + `StartMinute` + `Duration` (minuti od
 * UTC ponoći) — prebacuju se u lokalni dan i lokalni minut.
 *
 * Segment koji pređe LOKALNU ponoć se SEČE na dva reda, po jedan za svaki dan;
 * inače bi traka na dijagramu curila preko ivice ose ili nestala.
 *
 * Dužina dana se ne pretpostavlja kao 24h — na prelazu na letnje/zimsko vreme
 * lokalni dan ima 23 ili 25 sati, pa se sledeća ponoć traži po datumu, ne
 * sabiranjem.
 */
export function segmentiULokalno(redovi, zona = ZONA) {
    const out = [];
    for (const r of (Array.isArray(redovi) ? redovi : [])) {
        const baza = Date.parse(String(r.ActivityDate || '').substring(0, 10) + 'T00:00:00.000Z');
        const traje = Number(r.Duration) || 0;
        if (!Number.isFinite(baza)) { out.push(r); continue; }

        let od = baza + (Number(r.StartMinute) || 0) * 60000;
        const kraj = od + traje * 60000;
        if (!(kraj > od)) { out.push({ ...r, StartMinute: lokalniMinut(od, zona), ActivityDate: lokalniDan(od, zona) + 'T00:00:00.000Z' }); continue; }

        let zastita = 0;
        while (od < kraj && zastita++ < 400) {
            const dan = lokalniDan(od, zona);
            const sutra = new Date(Date.parse(dan + 'T00:00:00.000Z') + 24 * 3600000)
                .toISOString().substring(0, 10);
            const sledecaPonoc = pocetakLokalnogDana(sutra, zona);
            const doo = Math.min(kraj, sledecaPonoc);
            if (!(doo > od)) break;
            out.push({
                ...r,
                ActivityDate: dan + 'T00:00:00.000Z',
                StartMinute: lokalniMinut(od, zona),
                Duration: Math.round((doo - od) / 60000),
            });
            od = doo;
        }
    }
    return out;
}

/** Isto za proizvoljno polje sa vremenom (mesta, vozila, događaji). */
export function poljeULokalno(red, polja, zona = ZONA) {
    if (!red) return red;
    const out = { ...red };
    for (const p of polja) {
        const ms = Date.parse(out[p]);
        if (Number.isFinite(ms)) out[p] = lokalniIso(ms, zona) + '.000Z';
    }
    return out;
}

/* ── opseg upita ──────────────────────────────────────────────────────────── */

/** Dan „YYYY-MM-DD" pomeren za n dana po kalendaru (bez zone — ovo je samo datum). */
export function pomeriDan(dan, n) {
    const ms = Date.parse(String(dan).substring(0, 10) + 'T00:00:00.000Z');
    return Number.isFinite(ms) ? new Date(ms + n * 86400000).toISOString().substring(0, 10) : '';
}

/**
 * Opseg koji se TRAŽI od baze širi je od onog koji se PRIKAZUJE.
 *
 * Baza drži UTC dane, a prikaz ide po lokalnoj zoni, pa svaki lokalni dan
 * zahvata DVA UTC dana: lokalni 01.06 počinje 31.05 u 22:00 UTC i završava se
 * 01.06 u 22:00 UTC. Ako se traži tačno [01.06, 30.06], prva dva sata 01.06 i
 * poslednja dva sata 30.06 nikad ne budu učitana — na dijagramu se vidi
 * praznina na početku prvog i na kraju poslednjeg dana.
 *
 * Zato se upit širi za jedan dan sa svake strane (dovoljno za svaku zonu —
 * najveći pomak na svetu je 14 sati), a prikaz se posle skraćuje na traženi
 * opseg kroz `uOpsegu`.
 */
export function opsegZaUpit(od, doo) {
    return {
        dateFrom: od ? pomeriDan(od, -1) : od,
        dateTo: doo ? pomeriDan(doo, 1) : doo,
    };
}

/** Da li lokalni dan „YYYY-MM-DD" pada u prikazani opseg (granice uključene). */
export function uOpsegu(dan, od, doo) {
    const d = String(dan || '').substring(0, 10);
    if (!d) return false;
    if (od && d < String(od).substring(0, 10)) return false;
    if (doo && d > String(doo).substring(0, 10)) return false;
    return true;
}
